import { useEffect, useState } from 'react'
import { verifyMagicLink, setSession, ApiError } from '../api'

/**
 * Handles the /verify?token=… landing from the magic-link email.
 *
 * Reads the token from window.location.search, exchanges it for a session JWT,
 * then calls onSuccess() to bounce the App router to the Home screen.
 */
export function Verify({ onSuccess }: { onSuccess: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(true)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (!token) {
      setError('Missing sign-in token. Try requesting a new link.')
      setPending(false)
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const { token: jwt, email } = await verifyMagicLink(token)
        if (cancelled) return
        setSession(jwt, email)
        // Strip the path + search so:
        //   1. Refresh doesn't re-burn the token (already single-use, but
        //      avoids the confusing "invalid link" error on refresh).
        //   2. App.tsx's currentRoute() doesn't re-detect /verify on
        //      re-render and loop back into the verify state.
        window.history.replaceState({}, '', '/')
        onSuccess()
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof ApiError ? err.message : 'Could not verify the link. Please try again.'
        setError(msg)
        setPending(false)
      }
    })()

    return () => { cancelled = true }
    // onSuccess is stable in our App but exhaustive-deps would still flag — fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (pending) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 36, height: 36,
            border: '3px solid var(--color-brand-light)',
            borderTopColor: 'var(--color-brand)',
            borderRadius: '50%',
            margin: '12px auto 20px',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>Signing you in…</h1>
        <p style={{ margin: 0, color: 'var(--color-gray-700)' }}>This will only take a moment.</p>
      </div>
    )
  }

  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: 'var(--color-error-bg)',
        color: 'var(--color-error)',
        margin: '8px auto 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 26, fontWeight: 800,
      }}>!</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>
        We couldn't sign you in
      </h1>
      <p style={{ margin: '0 0 20px', color: 'var(--color-gray-700)' }}>{error}</p>
      <a href="#/" className="btn-primary" style={{ display: 'inline-flex' }}>
        Request a new link
      </a>
    </div>
  )
}
